import { statusBadge } from '@vitesse/base-ui/components/templates';
import { t } from '@vitesse/base-ui/i18n';
import type { Camera } from '../../../core/domain/camera';
import type { CameraStreamState } from './camera-stream-manager';

/*
 * The camera's live status badges — rendered IDENTICALLY on the card and on the
 * detail-page header (BasePage badges slot), NEVER overlaid on the video. They
 * replace+augment the old descriptor-only status badge: the connection badge
 * reflects THIS browser's media state (Connected / Connecting / …), and live
 * bitrate + latency ride alongside it as blue pills.
 *
 * The stats come from the shared CameraStreamManager handle (the same one the
 * <vitesse-camera-feed> observes); the feed forwards them up via a `feedstats`
 * event so the card/detail can render them as proper base badges. All labels are
 * i18n; bitrate is formatted dynamically (kbps → Mbps as it grows).
 */

export interface CameraFeedStats {
  state: CameraStreamState;
  bitrateKbps: number;
  rttMs: number;
}

type Badge = ReturnType<typeof statusBadge>;

/** Collapse a kbps figure into the largest sensible unit with few digits:
 *  980 → "980 kbps", 10749 → "10.7 Mbps", 2_400_000 → "2.4 Gbps". */
export function formatBitrate(kbps: number): string {
  if (kbps >= 1_000_000) return `${(kbps / 1_000_000).toFixed(1)} Gbps`;
  if (kbps >= 1_000) return `${(kbps / 1_000).toFixed(1)} Mbps`;
  return `${Math.round(kbps)} kbps`;
}

export function cameraStatusBadges(camera: Camera | undefined, stats: CameraFeedStats | undefined): Badge[] {
  const out: Badge[] = [];
  // The PRIMARY badge reflects the CAMERA — the descriptor's producer-feeding
  // lifecycle ("is the camera online?") — NOT this browser's per-view media state.
  // So it stays "Connected" on the Recording/Properties tab, where the live feed is
  // paused (off-screen → look-away pause → its state goes idle) yet the camera is
  // still online and one tab-toggle away. The per-view connecting/reconnecting is
  // surfaced on the video surface itself (the feed's own overlay), never here.
  switch (camera?.status) {
    case 'error':
      out.push(statusBadge('red', t('camera.statusError')));
      break;
    case 'live':
      out.push(statusBadge('green', t('camera.statusConnected')));
      break;
    default: // 'idle' (no producer feeding) or not-yet-loaded
      out.push(statusBadge('gray', t('camera.statusIdle')));
  }
  // Live-quality pills ride alongside ONLY while actively watching (stats present);
  // they naturally fall away on the non-live tabs, leaving just "Connected".
  if (stats && stats.bitrateKbps > 0) out.push(statusBadge('blue', formatBitrate(stats.bitrateKbps)));
  if (stats && stats.rttMs > 0) out.push(statusBadge('blue', `${stats.rttMs} ms`));
  return out;
}
